import React, {useEffect, useState, useContext} from 'react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {Flex, Stack, useMultiStyleConfig, Button} from '@chakra-ui/react'
import PricingDetails from './pricing-details'
import {getOrderStatus} from '../utils'
import ItemList from '../../../../components/item-list'
import OrderItemCard from './order-item-card'
import MultipleShipments from './multiple-shipments'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getValidToken} from '../../../../utils/utils'
import {MRT_API_BASEPATH} from '../../../../ssr/constants'
import {remCalc} from '../../../../theme/_utils'
import {OrderContext} from '../order-context'
const OrderBodySection = ({order, refetchOrder}) => {
    const isSingleShipmentOrder = !order?.fulfillmentOrder || order?.fulfillmentOrder.length === 0
    const styles = useMultiStyleConfig('Orders', {isSingleShipmentOrder})
    const {status} = getOrderStatus(order?.status)
    const {activeReturnShipmentId} = useContext(OrderContext)

    const [fiscalDocuments, setFiscalDocuments] = useState([])
    const appOrigin = useOrigin(false)

    useEffect(() => {
        const fetchFiscalDocuments = async () => {
            try {
                const apiUrl = `${appOrigin}${MRT_API_BASEPATH}/get-auth-token-oms`
                const accessTokenKey = 'accessTokenOMS'
                const tokenExpirationKey = 'tokenExpirationOMS'

                const accessToken = await getValidToken(apiUrl, accessTokenKey, tokenExpirationKey)

                let documentsMap = {}

                // Fetch all fiscal documents (from order.fiscalDocuments)
                const fiscalDocs = order?.fiscalDocuments || []
                const shipments = order?.fulfillmentOrder || []

                // Case 1: No shipments, use first available fiscal document
                if (isSingleShipmentOrder && fiscalDocs.length > 0) {
                    const documentId = fiscalDocs[0]?.Id
                    if (documentId) {
                        const queryParams = new URLSearchParams({documentId})
                        const response = await fetch(
                            `${appOrigin}${MRT_API_BASEPATH}/retrieve-fiscal-document-details-oms?${queryParams.toString()}`,
                            {
                                method: 'GET',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${accessToken}`,
                                },
                            },
                        )

                        if (response.ok) {
                            const blob = await response.blob()
                            const url = window.URL.createObjectURL(blob)
                            documentsMap['single'] = url
                        }
                    }
                }

                // Case 2: Multiple shipments, assign fiscal documents based on Name match with ExternalId__c
                if (shipments.length > 0 && fiscalDocs.length > 0) {
                    const documentPromises = shipments.map(async (shipment) => {
                        const document = fiscalDocs.find(
                            (invoice) =>
                                invoice?.Name?.split('_').pop() === shipment?.ExternalId__c,
                        ) // Match based on the last part of Name and ExternalId__c

                        if (!document) return null // Stop assigning if no matching document

                        const queryParams = new URLSearchParams({documentId: document.Id})
                        const response = await fetch(
                            `${appOrigin}${MRT_API_BASEPATH}/retrieve-fiscal-document-details-oms?${queryParams.toString()}`,
                            {
                                method: 'GET',
                                headers: {
                                    'Content-Type': 'application/json',
                                    Authorization: `Bearer ${accessToken}`,
                                },
                            },
                        )

                        if (response.ok) {
                            const blob = await response.blob()
                            const url = window.URL.createObjectURL(blob)
                            return {name: document.Name, documentUrl: url}
                        }
                        return null
                    })

                    const results = await Promise.all(documentPromises)
                    documentsMap = results.filter((doc) => doc !== null)
                }

                setFiscalDocuments(documentsMap)
            } catch (error) {
                console.error('Error retrieving fiscal document details:', error)
            }
        }

        fetchFiscalDocuments()
    }, [order])

    return (
        <Stack {...styles.shipmentWrapper}>
            {isSingleShipmentOrder ? (
                <Stack {...styles.borderWrapper}>
                    <Flex {...styles.singleShipmentContainer}>
                        {fiscalDocuments['single'] && order?.orderItems?.length > 0 && (
                            <Button
                                variant="secondary"
                                as="a"
                                href={fiscalDocuments['single']}
                                download={order?.fiscalDocuments?.[0]?.Name}
                                width={remCalc(181)}
                            >
                                <FormattedMessage
                                    id="order_details_download_receipt"
                                    defaultMessage="Download the receipt"
                                />
                            </Button>
                        )}
                        <ItemList
                            items={order?.orderItems}
                            renderItem={(item, index) => (
                                <OrderItemCard
                                    key={item?.Name || index}
                                    item={item}
                                    status={status}
                                />
                            )}
                        />
                    </Flex>
                    {!activeReturnShipmentId && <PricingDetails order={order} />}
                </Stack>
            ) : (
                <MultipleShipments
                    order={order}
                    fiscalDocuments={fiscalDocuments}
                    refetchOrder={refetchOrder}
                />
            )}
        </Stack>
    )
}

OrderBodySection.propTypes = {
    order: PropTypes.object,
    refetchOrder: PropTypes.func,
}

export default OrderBodySection
